import React, { useEffect, useState } from "react";
import {
  User,
  LockKeyhole,
  Phone,
  Coins,
  Eye,
  EyeOff,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./RegisterModal.css";
import { createPlayer, login } from "../../api/auth.api";
import { showToast } from "../../components/toast";
import { getBrandName } from "../../common/brand";
import Logo from "../../common/Logo";
const defaultFormData = {
  username: "",
  password: "",
  confirmPassword: "",
  phone: "",
  currency: "BDT",
  agreed: false,
  referral_code: "",
  affiliate_code: "",
  agent_code: "",
};

export default function RegisterModal({
  isOpen = true,
  onClose,
  onRegister,
  onLogin,
  logoSrc,
}) {
  const navigate = useNavigate();
  const brandName = getBrandName();

  const [formData, setFormData] = useState(defaultFormData);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchedLogo, setFetchedLogo] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setFormData((prev) => ({
      ...prev,
      agent_code: localStorage.getItem("agent_code") || "",
      referral_code: localStorage.getItem("referral_code") || "",
      affiliate_code: localStorage.getItem("affiliate_code") || "",
    }));

    if (!logoSrc) {
      const fetchLogo = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/logo`);
          if (!response.ok) throw new Error("Failed to fetch logo");
          const data = await response.json();
          setFetchedLogo(data?.[0]?.mobile_logo || "");
        } catch (error) {
          console.error("Error fetching logo:", error);
        }
      };

      fetchLogo();
    }
  }, [isOpen, logoSrc]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleReset = () => {
    setFormData({
      ...defaultFormData,
      agent_code: localStorage.getItem("agent_code") || "",
      referral_code: localStorage.getItem("referral_code") || "",
      affiliate_code: localStorage.getItem("affiliate_code") || "",
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      showToast("Username is required", "error");
      return;
    }

    if (!formData.password) {
      showToast("Password is required", "error");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }

    if (!formData.phone.trim()) {
      showToast("Phone number is required", "error");
      return;
    }

    if (!formData.agreed) {
      showToast("Please accept the user agreement", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await createPlayer({
        username: formData.username,
        password: formData.password,
        phone: formData.phone,
        referral_code: formData.referral_code || undefined,
        affiliate_code: formData.affiliate_code || undefined,
        agent_code: formData.agent_code || undefined,
      });

      if (res?.data?.success) {
        const loginRes = await login({
          username: formData.username,
          password: formData.password,
        });

        if (loginRes?.data?.status) {
          localStorage.setItem("token", loginRes.data.token);
          localStorage.setItem("remote_id", loginRes.data.remote_id);
          localStorage.setItem("username", loginRes.data.username);
          localStorage.setItem("popup", 1);
          localStorage.setItem("regPopup", 1);

          localStorage.removeItem("agent_code");
          localStorage.removeItem("referral_code");
          localStorage.removeItem("affiliate_code");

          showToast("Registration successful!", "success");

          onRegister?.(res.data);
          handleReset();
          onClose?.();
          navigate("/home");
          window.location.reload()
        } else {
          showToast(
            loginRes?.data?.message || "Registration successful. Please log in.",
            "success"
          );
          onClose?.();
          onLogin?.();
        }
      } else {
        const errors = res?.data?.errors;
        const firstError =
          typeof errors === "object"
            ? Object.values(errors).flat()[0]
            : errors;

        showToast(firstError || res?.data?.message || "Registration failed", "error");
      }
    } catch (err) {
      console.error("Signup error:", err?.response?.data || err?.message);
      showToast(err?.response?.data?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    onClose?.();
    onLogin?.();
  };

  const finalLogo =
    logoSrc ||
    (fetchedLogo
      ? `${process.env.REACT_APP_API_URL}/storage/${fetchedLogo}`
      : "");

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div
        className="login-modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Register modal"
      >
        <button
          type="button"
          className="login-modal-close"
          onClick={onClose}
          aria-label="Close modal"
        >
          <X size={28} strokeWidth={2.6} />
        </button>

        <div className="login-modal-header">
          <div className="login-modal-logo-text">
            <Logo width={80} height={70}/>
          </div>
        </div>

        <div className="register-divider" />

        <form className="login-modal-form register-form-spacing" onSubmit={handleSubmit}>
          <div className="login-input-wrap">
            <User className="login-input-icon" size={22} strokeWidth={2.4} />
            <input
              type="text"
              placeholder="Username"
              className="login-input"
              value={formData.username}
              onChange={(e) => handleChange("username", e.target.value)}
            />
          </div>

          <div className="login-input-wrap">
            <LockKeyhole className="login-input-icon" size={22} strokeWidth={2.4} />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className="login-input"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff size={22} strokeWidth={2.4} />
              ) : (
                <Eye size={22} strokeWidth={2.4} />
              )}
            </button>
          </div>

          <div className="login-input-wrap">
            <LockKeyhole className="login-input-icon" size={22} strokeWidth={2.4} />
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              className="login-input"
              value={formData.confirmPassword}
              onChange={(e) => handleChange("confirmPassword", e.target.value)}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
            >
              {showConfirmPassword ? (
                <EyeOff size={22} strokeWidth={2.4} />
              ) : (
                <Eye size={22} strokeWidth={2.4} />
              )}
            </button>
          </div>

          <div className="login-input-wrap">
            <Phone className="login-input-icon" size={22} strokeWidth={2.4} />
            <input
              type="tel"
              placeholder="Phone Number"
              className="login-input"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
          </div>

          <div className="login-input-wrap">
            <Coins className="login-input-icon" size={22} strokeWidth={2.4} />
            <input
              type="text"
              placeholder="BDT"
              className="login-input"
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              readOnly
            />
          </div>

          <label className="remember-row">
            <input
              type="checkbox"
              checked={formData.agreed}
              onChange={() => handleChange("agreed", !formData.agreed)}
            />
            <span className="custom-checkbox" />
            <span className="remember-text">Read and agree to the user agreement</span>
          </label>

          <button type="submit" className="auth-main-btn" disabled={loading}>
            {loading ? "Registering..." : "Register"}
          </button>

          <button
            type="button"
            className="auth-outline-btn"
            onClick={handleLoginClick}
            disabled={loading}
          >
            Log In
          </button>
        </form>
      </div>
    </div>
  );
}